import {
  IconAlertCircle,
  IconUser,
  IconUserCheck,
  IconUserUp,
} from "@tabler/icons-react";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { useSWRConfig } from "swr";
import ErrMessage from "../components/core/ErrMessage.tsx";
import { LOADING_CIRCLE } from "../components/core/loading/LoadingCircle.tsx";
import SEO from "../components/core/SEO.tsx";
import Footer from "../components/Footer.tsx";
import UserForm from "../components/user/form/UserForm.tsx";
import UserCompact from "../components/user/view/UserCompact.tsx";
import { ROUTE_PROFILE } from "../const/routes.ts";
import { EMPTY_USER_PROFILE } from "../const/typeDefault/User.ts";
import { USER_URL } from "../fetch/const.routes.ts";
import { putUser, useUser } from "../fetch/fetch.user.ts";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { useSubmitState } from "../hooks/useSubmitState";
import ErrorMap from "../types/Error/ErrorIcons.tsx";
import type { UserType } from "../types/User.ts";

const initButton = (
  <>
    <IconUserUp />
    Text speichern
  </>
);
const loadingButton = (
  <>
    {LOADING_CIRCLE}
    Lade hoch
  </>
);
const successButton = (
  <>
    <IconUserCheck />
    Hochgeladen
  </>
);

function UserPutPage() {
  const userForm = useForm<UserType>({
    mode: "all",
  });
  const submitState = useSubmitState({ initButtonChild: initButton });
  const navigate = useNavigate();
  const { mutate } = useSWRConfig();
  const currentUser = useCurrentUser();
  const { user, isLoading, isError } = useUser(currentUser, EMPTY_USER_PROFILE);

  useEffect(() => {
    const defaultForm = {
      ...user,
    };
    userForm.reset(defaultForm);
  }, [user]);

  if (isLoading) return LOADING_CIRCLE;
  if (isError) return <ErrMessage message={isError.message} />;

  const handleUserUpdate = async (data: UserType) => {
    submitState.init(loadingButton);
    // Create update user object
    const updateUser = {
      ...data,
      _id: user._id,
      username: data.username,
      description: data.description,
      avatarUrl: data.avatarUrl || undefined,
    };

    const res = await putUser(updateUser);
    if (!res.ok) {
      submitState.fetchFailed(res.error, <ErrorMap error={isError} />);
      return;
    }
    await mutate(`${USER_URL}${user._id}`, updateUser);

    submitState.fetchSuccess(successButton);

    // Navigate to the user profile or dashboard
    navigate(ROUTE_PROFILE(user._id), {
      replace: true,
    });
  };

  return (
    <div className="safe-bottom-with-dock safe-top-with-hanger flex flex-col gap-4 p-page items-center justify-center">
      <SEO
        title="Profil bearbeiten"
        description="Bearbeite dein Profil. "
        url={ROUTE_PROFILE(currentUser)}
      />
      {/* Main Content */}
      <main className="max-w-4xl w-full min-h-screen">
        <h1 className="divider">Bearbeite dein Profil</h1>

        <div>
          <div className="w-full">
            <h2 className="badge text-2xl h-full font-semibold text-start mb-4">
              <IconAlertCircle /> Das Profil ist öffentlich sichtbar
            </h2>
          </div>
          <UserForm
            onSubmit={handleUserUpdate}
            userForm={userForm}
            submitState={submitState}
          >
            <button
              type="submit"
              className={`btn btn-primary w-full`}
              disabled={!userForm.formState.isValid}
            >
              {submitState.buttonChild}
            </button>
          </UserForm>
        </div>
        <div className="divider">Aktuelles Profil</div>
        <div className="rounded-box bg-base-200 w-full p-0.5">
          <UserCompact userId={currentUser}>
            <Link to={ROUTE_PROFILE(currentUser)} className={`btn btn-ghost`}>
              <IconUser /> Anzeigen
            </Link>
          </UserCompact>
        </div>
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
}

export default UserPutPage;
